import { Box, Grid, Link, SvgIcon, Tooltip } from '@mui/material';
import AGGridComponent from 'components/AgGrid/AGGridComponent';
import { ColDef, ColGroupDef, ICellRendererParams } from 'ag-grid-community';
import { openInNewTab } from 'utils/browserUtils';
import { useNavigate } from 'react-router';
import { getPartnerName } from 'utils/stringUtils';
import { PartnerSearchResponse } from 'pco-product-service-internal-consumer';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import ApproveIcon from 'assets/icons/agreement-status/approved-status-icon.svg?react';
import EyeIcon from 'assets/icons/eye.svg?react';
import InApproval from 'assets/icons/agreement-status/in-approval-status-icon.svg?react';
import Deleted from 'assets/icons/agreement-status/deleted-status-icon.svg?react';
import RejectedIcon from 'assets/icons/agreement-status/rejected-status-icon.svg?react';
import RequestClarificationIcon from 'assets/icons/agreement-status/clarification-needed-status-icon.svg?react';
import Inactive from 'assets/icons/agreement-status/inactive-status-icon.svg?react';
import InitiatedIcon from 'assets/icons/agreement-status/initiated-status-icon.svg?react';

interface ServiceRoleDisplay {
  serviceAgreementUid?: string;
  serviceRoleUid?: string;
  partnerUid?: string;
  brUid?: string;
  partnerNumber?: string;
  status?: string;
  firstName?: string;
  lastName?: string;
}

type Props = {
  serviceRoles: ServiceRoleDisplay[];
};

const ServiceRoles = ({ serviceRoles }: Props) => {
  const CLM_URL = useSelector((state: RootState) => state.appConfig.CLM_URL);
  const navigate = useNavigate();

  const handleClick = (params: ICellRendererParams<ServiceRoleDisplay>) =>
    navigate(`serviceRole/${params.data?.serviceRoleUid}/details`);

  const userFriendlyStatus = (status: string) => {
    switch (status) {
      case 'INITIATED':
        return 'Initiated';
      case 'IN_APPROVAL':
        return 'In Approval';
      case 'REJECTED':
        return 'Rejected';
      case 'CLARIFICATION_NEEDED':
        return 'Clarification needed';
      case 'INACTIVE':
        return 'Inactive';
      case 'DELETED':
        return 'Deleted';
      case 'ACTIVE':
        return 'active';
      case 'APPROVED':
        return 'Approved';
      case 'VALIDATION_COMPLETED':
        return 'Validation Completed';
      case 'IN_VALIDATION':
        return 'In Validation';
      default:
        return 'Not Available';
    }
  };

  const renderIconBasedOnStatus = (status: string) => {
    switch (status) {
      case 'INITIATED':
        return <InitiatedIcon />;
      case 'IN_APPROVAL':
        return <InApproval />;
      case 'REJECTED':
        return <RejectedIcon />;
      case 'CLARIFICATION_NEEDED':
        return <RequestClarificationIcon />;
      case 'INACTIVE':
        return <Inactive />;
      case 'DELETED':
        return <Deleted />;
      case 'ACTIVE':
        return <ApproveIcon />;
      case 'VALIDATION_COMPLETED':
        return <ApproveIcon />;
      case 'IN_VALIDATION':
        return <InApproval />;
      case 'APPROVED':
        return <ApproveIcon />;
    }
  };

  const columnDefs: (ColDef | ColGroupDef)[] = [
    {
      headerName: 'Service Agreement UID',
      field: 'serviceAgreementUid',
      hide: true,
    },
    {
      headerName: 'Name',
      cellRenderer: (params: ICellRendererParams<ServiceRoleDisplay>) => {
        return (
          <Link
            onClick={() => openInNewTab(params.data?.partnerUid, params.data?.brUid, CLM_URL)}
            sx={{
              display: 'flex',
              color: 'gray',
              cursor: 'pointer',
            }}
          >
            {getPartnerName(params.data as unknown as PartnerSearchResponse)}
          </Link>
        );
      },
    },
    {
      headerName: 'Partner ID',
      field: 'partnerNumber',
      minWidth: 90,
    },
    {
      headerName: 'Status',
      field: 'status',
      cellRenderer: (params: ICellRendererParams<ServiceRoleDisplay>) => {
        const status = params.data?.status ?? '';
        return (
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Box
              style={{
                display: 'grid',
                paddingRight: '0.5em',
              }}
            >
              {renderIconBasedOnStatus(status)}
            </Box>
            <Box> {userFriendlyStatus(status).charAt(0).toUpperCase() + userFriendlyStatus(status).slice(1)}</Box>
          </Box>
        );
      },
    },
    {
      headerName: '',
      field: 'id',
      minWidth: 70,
      maxWidth: 100,
      filter: false,
      cellRenderer: (params: ICellRendererParams<ServiceRoleDisplay>) => {
        return (
          <Tooltip title="View Details" arrow placement="top">
            <SvgIcon
              onClick={() => handleClick(params)}
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                color: 'gray',
                cursor: 'pointer',
                mt: 1,
              }}
            >
              <EyeIcon />
            </SvgIcon>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Grid data-testid="service-roles" container item xs={12} sx={{ display: 'flex', height: '100%', width: '100%' }}>
      <AGGridComponent
        id={'serviceRoles'}
        data-testid="service-roles-table"
        disableSelection={true}
        rowKey={'serviceRoleUid'}
        getColumnDefs={columnDefs}
        items={serviceRoles as unknown as Record<string, unknown>[]}
      />
    </Grid>
  );
};

export default ServiceRoles;
